import { useState, useEffect, useCallback, useRef } from 'react';
import type { DragEvent, ChangeEvent, KeyboardEvent } from 'react';
import { api } from '../../lib/api';
import { bus } from '../../App';
import { useSelectionStore } from '../../state/selectionStore';
import type { SearchResult as SelectionSearchResult } from '../../state/selectionStore';
import { quickRerank } from '../../../server/lib/tools';
import type { AgentRecord } from '../../../shared/types/agents';

interface FileNode {
  id: string;
  type: 'file' | 'dir';
  name: string;
  path: string;
  children?: FileNode[];
}

type DropTarget =
  | { indicatorId: 'root'; targetDir: ''; kind: 'root' }
  | { indicatorId: string; targetDir: string; kind: 'dir' | 'file' };

const TEXT_FILE_EXTENSIONS = new Set([
  'c',
  'cc',
  'css',
  'csv',
  'html',
  'htm',
  'js',
  'json',
  'jsx',
  'md',
  'sass',
  'scss',
  'svg',
  'ts',
  'tsx',
  'txt',
  'xml',
  'yaml',
  'yml'
]);

const ZIP_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed'
]);

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_RESULT_LIMIT = 40;

function getParentDirectory(path: string): string {
  if (!path.includes('/')) {
    return '';
  }
  const parts = path.split('/');
  parts.pop();
  return parts.join('/');
}

function hasFilePayload(event: DragEvent) {
  const types = Array.from(event.dataTransfer?.types ?? []);
  return types.includes('Files');
}

function getFileExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1 || lastDot === name.length - 1) {
    return '';
  }
  return name.slice(lastDot + 1).toLowerCase();
}

function isTextLikeFile(file: File): boolean {
  if (file.type && (file.type.startsWith('text/') || file.type === 'application/json' || file.type.endsWith('+xml'))) {
    return true;
  }
  const extension = getFileExtension(file.name);
  if (!extension) {
    return false;
  }
  return TEXT_FILE_EXTENSIONS.has(extension);
}

function isZipArchive(file: File): boolean {
  if (file.type && ZIP_MIME_TYPES.has(file.type)) {
    return true;
  }
  return getFileExtension(file.name) === 'zip';
}

function buildFileTree(paths: string[]): FileNode {
  const root: FileNode = {
    id: 'root',
    type: 'dir',
    name: '/',
    path: '',
    children: []
  };

  for (const path of paths) {
    const parts = path.split('/').filter(p => p.length > 0);
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      const currentPath = parts.slice(0, i + 1).join('/');

      if (!current.children) {
        current.children = [];
      }

      let child = current.children.find(c => c.name === part);

      if (!child) {
        child = {
          id: currentPath,
          type: isFile ? 'file' : 'dir',
          name: part,
          path: currentPath,
          children: isFile ? undefined : []
        };
        current.children.push(child);
      }

      if (!isFile) {
        current = child;
      }
    }
  }

  return root;
}

function collectAllDirIds(node: FileNode): string[] {
  const ids: string[] = [];
  if (node.type === 'dir') {
    ids.push(node.id);
    if (node.children) {
      for (const child of node.children) {
        ids.push(...collectAllDirIds(child));
      }
    }
  }
  return ids;
}

function getSearchSnippet(result: SelectionSearchResult): { before: string; match: string; after: string } {
  const lineText = result.text ?? '';
  const column = typeof result.column === 'number' ? result.column : 1;
  const startIndex = Math.max(0, column - 1);
  const matchLength = typeof result.match === 'string' ? result.match.length : 0;
  const endIndex = Math.min(lineText.length, startIndex + matchLength);
  return {
    before: lineText.slice(0, startIndex),
    match: matchLength > 0 ? lineText.slice(startIndex, endIndex) : '',
    after: lineText.slice(matchLength > 0 ? endIndex : startIndex)
  };
}

function FileTreeNode({
  node,
  depth,
  isExpanded,
  toggle,
  onFileClick,
  selectedPath,
  dropTarget,
  setDropTarget,
  onFilesDrop,
  clearDragState
}: {
  node: FileNode;
  depth: number;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  onFileClick: (path: string) => void;
  selectedPath: string | null;
  dropTarget: DropTarget | null;
  setDropTarget: (target: DropTarget) => void;
  onFilesDrop: (targetDir: string, files: FileList) => Promise<void>;
  clearDragState: () => void;
}) {
  const dropIndicatorActive = dropTarget?.indicatorId === node.path;
  const indent = '  '.repeat(depth);

  if (node.type === 'file') {
    const isSelected = selectedPath === node.path;
    const parentDir = getParentDirectory(node.path);

    const handleFileDragEnter = (event: DragEvent<HTMLButtonElement>) => {
      if (!hasFilePayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDropTarget({
        indicatorId: node.path,
        targetDir: parentDir,
        kind: 'file'
      });
    };

    const handleFileDragOver = (event: DragEvent<HTMLButtonElement>) => {
      if (!hasFilePayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (dropTarget?.indicatorId !== node.path) {
        setDropTarget({
          indicatorId: node.path,
          targetDir: parentDir,
          kind: 'file'
        });
      }
    };

    const handleFileDrop = async (event: DragEvent<HTMLButtonElement>) => {
      if (!hasFilePayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        await onFilesDrop(parentDir, event.dataTransfer.files);
      } finally {
        clearDragState();
      }
    };

    const fileClasses = [
      'file-tree-item',
      isSelected ? 'file-tree-item--selected' : '',
      dropIndicatorActive ? 'file-tree-item--drop-target file-tree-item--drop-target-file' : ''
    ].filter(Boolean).join(' ');

    return (
      <li>
        <button
          type="button"
          onClick={() => onFileClick(node.path)}
          className={fileClasses}
          onDragEnter={handleFileDragEnter}
          onDragOver={handleFileDragOver}
          onDrop={handleFileDrop}
        >
          {indent}{node.name}
        </button>
      </li>
    );
  }

  const expanded = isExpanded(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const handleDirDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setDropTarget({
      indicatorId: node.path,
      targetDir: node.path,
      kind: 'dir'
    });
  };

  const handleDirDragOver = (event: DragEvent<HTMLButtonElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (dropTarget?.indicatorId !== node.path) {
      setDropTarget({
        indicatorId: node.path,
        targetDir: node.path,
        kind: 'dir'
      });
    }
  };

  const handleDirDrop = async (event: DragEvent<HTMLButtonElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    try {
      await onFilesDrop(node.path, event.dataTransfer.files);
    } finally {
      clearDragState();
    }
  };

  const dirClasses = [
    'file-tree-item',
    'file-tree-item--dir',
    dropIndicatorActive ? 'file-tree-item--drop-target file-tree-item--drop-target-dir' : ''
  ].filter(Boolean).join(' ');

  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => hasChildren && toggle(node.id)}
          className={dirClasses}
          onDragEnter={handleDirDragEnter}
          onDragOver={handleDirDragOver}
          onDrop={handleDirDrop}
        >
          {indent}{expanded ? '▾' : '▸'} {node.name}/
        </button>
      </li>
      {expanded && hasChildren && (
        <>
          {node.children!.map((child) => (
            <FileTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isExpanded={isExpanded}
              toggle={toggle}
              onFileClick={onFileClick}
              selectedPath={selectedPath}
              dropTarget={dropTarget}
              setDropTarget={setDropTarget}
              onFilesDrop={onFilesDrop}
              clearDragState={clearDragState}
            />
          ))}
        </>
      )}
    </>
  );
}

export function FileExplorerPanel() {
  const [fileTree, setFileTree] = useState<FileNode | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [dropTarget, setDropTargetState] = useState<DropTarget | null>(null);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SelectionSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const rootDragDepth = useRef(0);
  const filePath = useSelectionStore((state) => state.file_path);
  const filesystemId = useSelectionStore((state) => state.filesystem_id);
  const select = useSelectionStore((state) => state.set);
  const filePathRef = useRef<string | null>(filePath);
  const searchRequestIdRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedSearchQuery = searchQuery.trim();
  const isSearchMode = trimmedSearchQuery.length > 0;

  useEffect(() => {
    filePathRef.current = filePath;
  }, [filePath]);

  useEffect(() => {
    if (!trimmedSearchQuery) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    const requestId = ++searchRequestIdRef.current;
    setSearchLoading(true);
    setSearchError(null);

    const timeoutId = window.setTimeout(() => {
      const client = api();
      void (async () => {
        try {
          const matches = await client.services.files.rg(trimmedSearchQuery);
          if (cancelled || requestId !== searchRequestIdRef.current) {
            return;
          }
          const normalized = Array.isArray(matches)
            ? matches as SelectionSearchResult[]
            : [];
          const reranked = quickRerank(normalized, trimmedSearchQuery, SEARCH_RESULT_LIMIT) as SelectionSearchResult[];
          setSearchResults(reranked);
        } catch (error) {
          if (cancelled || requestId !== searchRequestIdRef.current) {
            return;
          }
          console.error('[FileExplorerPanel] Failed to search files:', error);
          const message = error instanceof Error ? error.message : 'Search failed';
          setSearchError(message || 'Search failed');
          setSearchResults([]);
        } finally {
          if (cancelled || requestId !== searchRequestIdRef.current) {
            return;
          }
          setSearchLoading(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [trimmedSearchQuery, filesystemId]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && searchQuery) {
      event.preventDefault();
      event.stopPropagation();
      setSearchQuery('');
    }
  };

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const handleSearchResultClick = useCallback((result: SelectionSearchResult) => {
    select({ file_path: result.path, search_result: result });
  }, [select]);

  useEffect(() => {
    let cancelled = false;

    const loadAgents = async () => {
      try {
        const list = await api().services.agent.list();
        if (cancelled) {
          return;
        }
        if (!Array.isArray(list)) {
          setAgents([]);
          return;
        }
        const sortedAgents = [...(list as AgentRecord[])].sort((a, b) => {
          const left = (a.name?.trim() || a.id).toLowerCase();
          const right = (b.name?.trim() || b.id).toLowerCase();
          if (left === right) {
            return 0;
          }
          return left < right ? -1 : 1;
        });
        setAgents(sortedAgents);
      } catch (error) {
        console.error('[FileExplorerPanel] Failed to load agents:', error);
      }
    };

    void loadAgents();

    const unsubscribeCreate = bus.on('agent/create', () => {
      void loadAgents();
    });
    const unsubscribeUpdate = bus.on('agent/update', () => {
      void loadAgents();
    });
    const unsubscribeDelete = bus.on('agent/delete', () => {
      void loadAgents();
    });

    return () => {
      cancelled = true;
      unsubscribeCreate();
      unsubscribeUpdate();
      unsubscribeDelete();
    };
  }, []);

  const applyDropTarget = useCallback((target: DropTarget) => {
    setDropTargetState(target);
  }, []);

  const clearDragState = useCallback(() => {
    rootDragDepth.current = 0;
    setDropTargetState(null);
  }, []);

  const handleFilesDrop = useCallback(async (targetDir: string, fileList: FileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return;
    }
    const zipFiles: File[] = [];
    const regularFiles: File[] = [];
    for (const file of files) {
      if (isZipArchive(file)) {
        zipFiles.push(file);
      } else {
        regularFiles.push(file);
      }
    }

    for (const file of zipFiles) {
      try {
        const archiveBytes = new Uint8Array(await file.arrayBuffer());
        await api().services.files.unzip(archiveBytes, targetDir || '');
      } catch (error) {
        console.error('[FileExplorerPanel] Failed to import zip:', file.name, error);
      }
    }

    for (const file of regularFiles) {
      try {
        const payload = isTextLikeFile(file)
          ? await file.text()
          : new Uint8Array(await file.arrayBuffer());
        const normalizedPath = targetDir ? `${targetDir}/${file.name}` : file.name;
        await api().services.files.create(normalizedPath, payload);
      } catch (error) {
        console.error('[FileExplorerPanel] Failed to import file:', file.name, error);
      }
    }
  }, []);

  const handlePanelDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    rootDragDepth.current += 1;
    setDropTargetState((current) => {
      if (current && current.indicatorId !== 'root') {
        return current;
      }
      return { indicatorId: 'root', targetDir: '', kind: 'root' };
    });
  }, []);

  const handlePanelDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    setDropTargetState((current) => {
      if (current?.indicatorId === 'root') {
        return current;
      }
      return { indicatorId: 'root', targetDir: '', kind: 'root' };
    });
  }, []);

  const handlePanelDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    rootDragDepth.current = Math.max(0, rootDragDepth.current - 1);
    if (rootDragDepth.current === 0) {
      setDropTargetState(null);
    }
  }, []);

  const handlePanelDrop = useCallback(async (event: DragEvent<HTMLDivElement>) => {
    if (!hasFilePayload(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    try {
      await handleFilesDrop('', event.dataTransfer.files);
    } finally {
      clearDragState();
    }
  }, [handleFilesDrop, clearDragState]);

  const handleFilesystemChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const nextId = event.target.value;
    if (nextId === filesystemId) {
      return;
    }
    select({ filesystem_id: nextId, search_result: null });
    await api().services.files.activateFilesystem(nextId);
  };

  const panelClassName = 'panel-shell file-explorer-panel';
  const isRootDropTarget = dropTarget?.indicatorId === 'root';
  const treeContainerClassName = isRootDropTarget
    ? 'file-explorer-panel__tree file-explorer-panel__tree--root-target'
    : 'file-explorer-panel__tree';

  const loadFiles = useCallback(async ({
    selectFirst = false,
    showSpinner = false
  }: {
    selectFirst?: boolean;
    showSpinner?: boolean;
  } = {}) => {
    if (showSpinner) {
      setLoading(true);
    }
    try {
      await api().services.files.activateFilesystem(filesystemId);
      const paths = await api().services.files.list();
      const normalizedPaths = Array.isArray(paths) ? paths as string[] : [];
      const tree = buildFileTree(normalizedPaths);
      setFileTree(tree);

      const allDirIds = collectAllDirIds(tree);
      setExpandedIds(new Set(allDirIds));

      if (normalizedPaths.length === 0) {
        select({ file_path: null, search_result: null });
      } else if (selectFirst) {
        select({ file_path: normalizedPaths[0], search_result: null });
      } else if (filePathRef.current && !normalizedPaths.includes(filePathRef.current)) {
        select({ file_path: null, search_result: null });
      }
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to load files:', error);
    } finally {
      setLoading(false);
    }
  }, [filesystemId, select]);

  useEffect(() => {
    void loadFiles({ showSpinner: true });
  }, [filesystemId, loadFiles]);

  useEffect(() => {
    const handleRefresh = () => {
      void loadFiles();
    };
    const unsubscribeCreate = bus.on('file/create', handleRefresh);
    const unsubscribeUpdated = bus.on('file/update', handleRefresh);
    const unsubscribeDelete = bus.on('file/delete', handleRefresh);
    const unsubscribeReset = bus.on('files/reset', handleRefresh);
    const unsubscribeCheckout = bus.on('files/checkout', () => {
      void loadFiles({ selectFirst: true });
    });

    return () => {
      unsubscribeCreate();
      unsubscribeUpdated();
      unsubscribeDelete();
      unsubscribeReset();
      unsubscribeCheckout();
    };
  }, [loadFiles]);

  const isExpanded = (id: string) => expandedIds.has(id);
  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleFileClick = (path: string) => {
    select({ file_path: path, search_result: null });
  };

  const handleReset = async () => {
    try {
      await api().services.files.reset();
      // The files/reset event will trigger loadFiles automatically
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to reset files:', error);
    }
  };

  const handleHost = async () => {
    try {
      await api().services.meta.delete('host_version_id');
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to delete host_version_id:', error);
    }
  };

  const handleSnapshot = async () => {
    try {
      const result = await api().services.files.snapshot();
      await api().services.files.updateVersion({ id: result.versionId, major: true });
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to create snapshot:', error);
    }
  };

  const handleDownloadZip = useCallback(async () => {
    setDownloadingZip(true);
    try {
      const archive = await api().services.files.zip();
      const bytes = archive instanceof Uint8Array
        ? archive
        : archive && typeof (archive as ArrayBuffer).byteLength === 'number'
          ? new Uint8Array(archive as ArrayBuffer)
          : null;

      if (!bytes) {
        throw new Error('zip() did not return binary data');
      }

      const blob = new Blob([bytes], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'files.zip';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to download zip:', error);
    } finally {
      setDownloadingZip(false);
    }
  }, []);

  const handleDownloadFile = useCallback(async () => {
    if (!filePath) {
      return;
    }
    try {
      const content = await api().services.files.read(filePath);
      let blob: Blob;

      if (content instanceof Uint8Array) {
        blob = new Blob([content]);
      } else if (typeof content === 'string') {
        blob = new Blob([content], { type: 'text/plain' });
      } else {
        throw new Error('Unexpected file content type');
      }

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filePath.split('/').pop() || 'file';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to download file:', error);
    }
  }, [filePath]);

  const handleDelete = useCallback(async () => {
    if (!filePath) {
      return;
    }
    try {
      await api().services.files.delete(filePath);
      // The file/delete event will trigger loadFiles automatically
      // Select first remaining file or null after delete
      const paths = await api().services.files.list();
      const normalizedPaths = Array.isArray(paths) ? paths as string[] : [];
      if (normalizedPaths.length > 0) {
        select({ file_path: normalizedPaths[0], search_result: null });
      } else {
        select({ file_path: null, search_result: null });
      }
    } catch (error) {
      console.error('[FileExplorerPanel] Failed to delete file:', error);
    }
  }, [filePath, select]);

  const panelControls = (
    <div className="panel-controls">
      <select
        value={filesystemId}
        onChange={handleFilesystemChange}
        className="input-surface file-explorer-panel__filesystem-select"
        aria-label="Select filesystem"
      >
        <option value="">Default filesystem</option>
        {agents.map((agent) => {
          const label = agent.name?.trim() || agent.id;
          return (
            <option key={agent.id} value={agent.id}>
              {label}
            </option>
          );
        })}
      </select>
      <button onClick={handleReset} className="btn">Reset</button>
      <button onClick={handleHost} className="btn">Host</button>
      <button onClick={handleSnapshot} className="btn">Snapshot</button>
      <button onClick={handleDownloadZip} className="btn" disabled={downloadingZip}>
        {downloadingZip ? 'Preparing Zip...' : 'Download Zip'}
      </button>
      <button onClick={handleDownloadFile} className="btn" disabled={!filePath}>
        Download
      </button>
      <button onClick={handleDelete} className="btn" disabled={!filePath}>
        Delete
      </button>
    </div>
  );

  const renderSearchResults = () => {
    if (searchLoading) {
      return <div className="file-explorer-panel__search-status">Searching...</div>;
    }
    if (searchError) {
      return (
        <div className="file-explorer-panel__search-status file-explorer-panel__search-status--error">
          {searchError}
        </div>
      );
    }
    if (!searchResults.length) {
      return <div className="file-explorer-panel__search-status">No matches</div>;
    }
    return (
      <ul className="file-explorer-panel__search-results-list">
        {searchResults.map((result) => {
          const snippet = getSearchSnippet(result);
          const key = `${result.path}:${result.line}:${result.column}:${result.match}`;
          return (
            <li key={key}>
              <button
                type="button"
                className="file-explorer-panel__search-result"
                onClick={() => handleSearchResultClick(result)}
              >
                <div className="file-explorer-panel__search-meta">
                  <span className="file-explorer-panel__search-path">{result.path}</span>
                  <span className="file-explorer-panel__search-line">
                    Line {result.line}, Col {result.column}
                  </span>
                </div>
                <div className="file-explorer-panel__search-snippet">
                  <span className="file-explorer-panel__search-snippet-text">{snippet.before}</span>
                  <span className="file-explorer-panel__search-match">{snippet.match || ' '}</span>
                  <span className="file-explorer-panel__search-snippet-text">{snippet.after}</span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const searchControls = (
    <div className="file-explorer-panel__search">
      <div className="file-explorer-panel__search-controls">
        <input
          ref={searchInputRef}
          type="search"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="prompt-library__search file-explorer-panel__search-input"
          placeholder="Search files"
          aria-label="Search files"
          spellCheck={false}
          autoComplete="off"
        />
        {searchQuery && (
          <button type="button" className="file-explorer-panel__search-clear" onClick={handleSearchClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div
        className={panelClassName}
        onDragEnter={handlePanelDragEnter}
        onDragOver={handlePanelDragOver}
        onDragLeave={handlePanelDragLeave}
        onDrop={handlePanelDrop}
      >
        <div className="panel-content panel-content--horizontal">
          <div className="empty-state">
            <p>Loading files...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!fileTree || !fileTree.children || fileTree.children.length === 0) {
    return (
      <div
        className={panelClassName}
        onDragEnter={handlePanelDragEnter}
        onDragOver={handlePanelDragOver}
        onDragLeave={handlePanelDragLeave}
        onDrop={handlePanelDrop}
      >
        {panelControls}
        {searchControls}
        {isSearchMode ? (
          <div className="file-explorer-panel__results">
            {renderSearchResults()}
          </div>
        ) : (
          <div className="panel-content panel-content--horizontal">
            <div className="empty-state">
              <p>No files</p>
              <p className="empty-state-subtitle">Files will appear here once created</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={panelClassName}
      onDragEnter={handlePanelDragEnter}
      onDragOver={handlePanelDragOver}
      onDragLeave={handlePanelDragLeave}
      onDrop={handlePanelDrop}
    >
      {panelControls}
      {searchControls}
      {isSearchMode ? (
        <div className="file-explorer-panel__results">
          {renderSearchResults()}
        </div>
      ) : (
        <div className={treeContainerClassName}>
          <ul className="file-tree-console">
            {fileTree.children.map((child) => (
              <FileTreeNode
                key={child.id}
                node={child}
                depth={0}
                isExpanded={isExpanded}
                toggle={toggle}
                onFileClick={handleFileClick}
                selectedPath={filePath}
                dropTarget={dropTarget}
                setDropTarget={applyDropTarget}
                onFilesDrop={handleFilesDrop}
                clearDragState={clearDragState}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
